import api from './api';
import { Task } from '../types';

// Priority enum mapping (must match backend Priority enum)
export enum Priority {
  Low = 1,
  Medium = 2,
  High = 3,
  Urgent = 4
}

export type PriorityString = 'low' | 'medium' | 'high' | 'urgent';

export function priorityToNumber(priority: PriorityString): number {
  const mapping: Record<PriorityString, number> = {
    'low': Priority.Low,
    'medium': Priority.Medium,
    'high': Priority.High,
    'urgent': Priority.Urgent
  };
  return mapping[priority] || Priority.Medium;
}

export interface CreateTaskData {
  projectId: string;
  title: string;
  description?: string;
  assigneeId?: string;
  priority: number;
  taskTypeId: string;
  parentTaskId?: string;
  startDate?: string;
  dueDate?: string;
  labelIds?: string[];
}

interface UpdateTaskData {
  title?: string;
  description?: string;
  assigneeId?: string;
  statusId?: string;
  priority?: number;
  startDate?: Date;
  dueDate?: Date;
  remainingEstimate?: number;
  labelIds?: string[];
}

interface TaskFilters {
  projectId?: string;
  assigneeId?: string;
  statusId?: string;
  priority?: PriorityString;
  searchTerm?: string;
  isOverdue?: boolean;
}

class TaskService {
  async getTasks(filters?: TaskFilters): Promise<Task[]> {
    const params = new URLSearchParams();
    if (filters?.projectId) params.append('projectId', filters.projectId);
    if (filters?.assigneeId) params.append('assigneeId', filters.assigneeId);
    if (filters?.statusId) params.append('statusId', filters.statusId);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.searchTerm) params.append('searchTerm', filters.searchTerm);
    if (filters?.isOverdue) params.append('isOverdue', 'true');

    const response = await api.get(`/tasks?${params.toString()}`);
    return response.data;
  }

  async getAllTasks(): Promise<Task[]> {
    const response = await api.get('/tasks');
    return response.data;
  }

  async getTask(id: string): Promise<Task> {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  }

  async createTask(data: CreateTaskData): Promise<Task> {
    const response = await api.post('/tasks', data);
    return response.data;
  }

  async updateTask(id: string, data: UpdateTaskData): Promise<Task> {
    const response = await api.put(`/tasks/${id}`, data);
    return response.data;
  }

  async deleteTask(id: string): Promise<void> {
    await api.delete(`/tasks/${id}`);
  }

  async getMyTasks(): Promise<Task[]> {
    const response = await api.get('/tasks/my-tasks');
    return response.data;
  }

  async getOverdueTasks(): Promise<Task[]> {
    const response = await api.get('/tasks/overdue');
    return response.data;
  }

  async getTaskTimeLogs(taskId: string): Promise<any[]> {
    const response = await api.get(`/tasks/${taskId}/time-logs`);
    return response.data;
  }

  async addTimeLog(taskId: string, data: {
    description?: string;
    duration: number;
    loggedAt: Date;
    workType?: string;
    isBillable: boolean;
  }): Promise<any> {
    const response = await api.post(`/tasks/${taskId}/time-logs`, data);
    return response.data;
  }
}

export const taskService = new TaskService();